import { Link } from "@tanstack/react-router";
import type { SafetyVideoStatus } from "@unbox/api";
import { ArrowUpRight, InfoCircle, Mail02, ShieldPlus, ShieldZap, VideoRecorder } from "@untitledui/icons";
import { Camera, MoreVertical } from "lucide-react";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useMerchant } from "@/providers/MerchantProvider";
import { videoExplorerRoute } from "@/router";
import { getCreatorName } from "@/utils/creator-name";
import { getCreatorSafetyStatus } from "@/utils/creator-safety";
import { NoInsightsEmptyState } from "../no-insights-empty-state";

type CreatorDetailsResponse = {
  creatorId: string;
  creatorName: string;
  nickname?: string | null;
  safetyAnalysis: {
    totalVideos: number;
    videosWithoutRisks: number;
    videosWithRisks: number;
    percentageAtRisk: number;
    videosWithTier1: number;
    videosWithTier2: number;
    percentageTier2AtRisk: number;
  };
  mostFrequentFlags: Array<{
    flagType: string;
    count: number;
    percentage: number;
  }>;
  videos: Array<{
    videoId: string;
    urlFirstFrame?: string | null;
    videoTitle?: string | null;
    videoUrl?: string | null;
    publishDate: string;
    flagCount: number;
    flagTypes: string[];
    flagTiers: number[];
    gmv: number;
    views: number;
    gpm: number;
    unitSales: number;
    productList?: string[];
    safetyStatus: SafetyVideoStatus;
    [key: string]: unknown; // Allow additional properties from the Video type
  }>;
};

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

type SafetyTabProps = {
  data: CreatorDetailsResponse;
  currencyFormatter: Intl.NumberFormat;
};

export const SafetyTab = ({ data, currencyFormatter }: SafetyTabProps) => {
  const merchant = useMerchant();
  const creatorNames = getCreatorName(data.nickname, data.creatorName);
  const isAssurance = useIsAssurance();

  const handleSupport = () => {
    window.location.href = "mailto:support@unbox-commerce.com";
  };

  const hasNoInsights =
    !data.safetyAnalysis ||
    (data.safetyAnalysis.totalVideos === 0 &&
      data.safetyAnalysis.videosWithoutRisks === 0 &&
      data.safetyAnalysis.videosWithRisks === 0 &&
      data.safetyAnalysis.percentageAtRisk === 0 &&
      data.safetyAnalysis.videosWithTier1 === 0 &&
      data.safetyAnalysis.videosWithTier2 === 0 &&
      data.safetyAnalysis.percentageTier2AtRisk === 0);

  if (hasNoInsights) {
    return (
      <NoInsightsEmptyState
        backgroundImage="/creators-safety-empty-state.png"
        align="left"
        className="aspect-[795/420]"
      />
    );
  }

  const safetyStatus = getCreatorSafetyStatus(data.safetyAnalysis);

  // Filter videos with risks (Brand Tagged only)
  const videosWithRisks = data.videos.filter((video) => video.flagCount > 0);
  const brandTaggedVideos = videosWithRisks.filter((video) => video.productList && video.productList.length > 0);

  // Check if creator has any risks
  const hasRisks = data.safetyAnalysis.videosWithRisks > 0;

  // If assurance is not enabled, show limited view with empty state
  if (!isAssurance) {
    return (
      <div
        className="relative p-8 pr-0 min-h-[30rem] flex items-center justify-center rounded-xl overflow-hidden"
        style={{
          background:
            "linear-gradient(90deg, rgba(173, 230, 255, 0.05) 0%, rgba(173, 230, 255, 0.1) 30%, rgba(255, 156, 222, 0.15) 50%, rgba(255, 194, 123, 0.15) 70%, rgba(255, 194, 123, 0.15) 100%)",
        }}
      >
        {/* Content */}
        <div className="relative flex gap-6 w-full">
          {/* Left Column: Empty State Text */}
          <div className="flex flex-col justify-center w-[45%] px-13">
            <h4 className="text-md font-semibold text-primary">Safety Insights not enabled</h4>
            <p className="text-sm text-secondary max-w-sm mt-2 mb-4">
              Want to know how safe this <br /> creator is for your brand? <br /> Unlock detailed risk analysis.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4 w-fit font-semibold bg-white p-5 border border-border-primary text-primary text-primary-gray"
              onClick={handleSupport}
            >
              <Mail02 className="w-5 h-5" />
              Get in touch
            </Button>
          </div>

          {/* Right Column: Safety Status + Analysis Table in white boxes */}
          <div className="space-y-6 flex-1 bg-white p-8 pr-0 rounded-l-[20px]">
            {/* Creator Safety Status - White Box */}
            <TooltipProvider>
              <div
                className={`flex items-center text-xs justify-between pl-4 py-2 border-[2px] rounded-l-lg border-r-0 bg-white ${
                  safetyStatus.color === "success"
                    ? "border-success-200"
                    : safetyStatus.color === "warning"
                      ? "border-warning-200"
                      : "border-error-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-medium ${
                      safetyStatus.color === "success"
                        ? "text-success-700"
                        : safetyStatus.color === "warning"
                          ? "text-[#F79009]"
                          : "text-error-700"
                    }`}
                  >
                    Creator safety status
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="cursor-help">
                        <InfoCircle
                          className={`w-4 h-4 ${
                            safetyStatus.color === "success"
                              ? "text-success-600"
                              : safetyStatus.color === "warning"
                                ? "text-[#F79009]"
                                : "text-error-600"
                          }`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm">{safetyStatus.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span
                  className={`pl-3 pr-1 py-1 rounded-l-md text-sm font-medium text-white ${
                    safetyStatus.color === "success"
                      ? "bg-success-600"
                      : safetyStatus.color === "warning"
                        ? "bg-[#F79009]"
                        : "bg-error-600"
                  }`}
                >
                  {safetyStatus.label}
                </span>
              </div>
            </TooltipProvider>

            {/* Analysis Table - White Box */}
            <div className="rounded-lg bg-white">
              <div className="space-y-0">
                <div className="flex items-center justify-between py-3 border-b border-border-primary">
                  <span className="text-sm text-tertiary"># Videos without risks</span>
                  <span className="text-sm font-medium text-primary pr-6">
                    {data.safetyAnalysis.videosWithoutRisks}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border-primary">
                  <span className="text-sm text-tertiary"># Videos with potential risks</span>
                  <span className="text-sm font-medium text-error-primary pr-6">
                    {data.safetyAnalysis.videosWithRisks}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border-primary">
                  <span className="text-sm text-tertiary">% Videos at risk</span>
                  <span className="text-sm font-medium text-primary pr-6">{data.safetyAnalysis.percentageAtRisk}%</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border-primary">
                  <span className="text-sm text-tertiary"># of videos analysed</span>
                  <span className="text-sm font-medium text-primary pr-6">{data.safetyAnalysis.totalVideos}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full view for merchants with assurance enabled
  return (
    <div className="space-y-6">
      {/* Two Column Layout: Common Violations (left 35%) + Safety Status & Analysis Table (right 65%) */}
      <div className="flex gap-6">
        {/* Left Column: Common Violations - Only show if there are risks */}
        {hasRisks && (
          <div className="space-y-4 w-[35%]">
            <div className="flex items-center gap-2 border-b border-border-primary pb-3">
              <ShieldPlus className="w-5 h-5 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Common violations</h3>
            </div>
            {data.mostFrequentFlags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.mostFrequentFlags.map((violation) => (
                  <Badge key={violation.flagType} color="gray">
                    {violation.flagType} - {violation.percentage}%
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-sm text-tertiary">No violations detected</div>
            )}
          </div>
        )}

        {/* Right Column: Safety Status + Analysis Table */}
        <div className="space-y-6 flex-1">
          {/* Creator Safety Status */}
          <TooltipProvider>
            <div
              className={`flex items-center justify-between px-4 py-2 border-[2px] rounded-lg w-full ${
                safetyStatus.color === "success"
                  ? "bg-success-50 border-success-200"
                  : safetyStatus.color === "warning"
                    ? "bg-warning-50 border-warning-200"
                    : "bg-error-50 border-error-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-medium ${
                    safetyStatus.color === "success"
                      ? "text-success-700"
                      : safetyStatus.color === "warning"
                        ? "text-[#F79009]"
                        : "text-error-700"
                  }`}
                >
                  Creator safety status
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="cursor-help">
                      <InfoCircle
                        className={`w-4 h-4 ${
                          safetyStatus.color === "success"
                            ? "text-success-600"
                            : safetyStatus.color === "warning"
                              ? "text-[#F79009]"
                              : "text-error-600"
                        }`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">{safetyStatus.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span
                className={`px-3 py-1 rounded-md text-sm font-medium text-white ${
                  safetyStatus.color === "success"
                    ? "bg-success-600"
                    : safetyStatus.color === "warning"
                      ? "bg-[#F79009]"
                      : "bg-error-600"
                }`}
              >
                {safetyStatus.label}
              </span>
            </div>
          </TooltipProvider>

          {/* Analysis Table */}
          <div className="space-y-0">
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary"># Videos without risks</span>
              <span className="text-sm font-medium text-primary pr-6">{data.safetyAnalysis.videosWithoutRisks}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary"># Videos with potential risks</span>
              <span className="text-sm font-medium text-error-primary pr-6">{data.safetyAnalysis.videosWithRisks}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary">% Videos at risk</span>
              <span className="text-sm font-medium text-primary pr-6">{data.safetyAnalysis.percentageAtRisk}%</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-border-primary">
              <span className="text-sm text-tertiary"># of videos analysed</span>
              <span className="text-sm font-medium text-primary pr-6">{data.safetyAnalysis.totalVideos}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Videos with Risks (Brand Tagged) - Only show if there are risks */}
      {hasRisks && (
        <div className="space-y-4 mt-15">
          <div className="flex items-center gap-2">
            <ShieldZap className="w-4 h-4 text-tertiary" />
            <h3 className="text-sm font-medium text-secondary">Videos with risks</h3>
          </div>
          {brandTaggedVideos.length > 0 ? (
            <div className="space-y-4">
              {brandTaggedVideos.map((video) => (
                <div
                  key={video.videoId}
                  className="border border-border-primary rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
                >
                  <div className="flex">
                    {/* Video Player or Thumbnail - Full height, no padding */}
                    <div className="relative w-32 flex-shrink-0 bg-black">
                      {video.videoUrl ? (
                        <video
                          src={video.videoUrl}
                          poster={video.urlFirstFrame || undefined}
                          controls
                          className="h-full object-cover aspect-[9/16] flex-shrink-0 bg-black"
                          preload="metadata"
                        />
                      ) : video.urlFirstFrame ? (
                        <img src={video.urlFirstFrame} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <Camera className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Right Column - Split into 2 sections */}
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* Section 1: Info and Menu (with padding) */}
                      <div className="p-4 flex-1">
                        {/* Title, Date, Status Tag and Menu */}
                        <div className="space-y-0.5 mb-3">
                          <div className="flex items-start gap-2">
                            {/* Clickable Title with dotted underline - truncated */}
                            <div className="flex-1">
                              <Link
                                to={videoExplorerRoute.to}
                                params={{ merchantId: merchant }}
                                search={{ video_id: video.videoId }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-primary underline decoration-dotted hover:text-primary/80 text-left truncate flex-1 min-w-0"
                              >
                                {video.videoTitle || "Untitled Video"}
                              </Link>
                              <p className="text-xs text-tertiary mb-3">
                                {dateFormatter.format(new Date(video.publishDate))}
                              </p>
                            </div>
                            {/* Status Badge - using DetailedVideoStatusBadge component with InfoCircle */}
                            <DetailedVideoStatusBadge status={video.safetyStatus} showInfoIcon={true} />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
                                  aria-label="Video actions"
                                >
                                  <MoreVertical className="w-4 h-4 text-gray-600" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" sideOffset={5}>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={videoExplorerRoute.to}
                                    params={{ merchantId: merchant }}
                                    search={{ video_id: video.videoId }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cursor-pointer flex gap-2"
                                  >
                                    <VideoRecorder className="w-4 h-4 text-tertiary" />
                                    See Video in Detail
                                  </Link>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        {/* Risks Identified */}
                        <div className="space-y-1.5 mb-4">
                          <p className="text-xs text-tertiary font-medium">Risks Identified</p>
                          <div className="flex flex-wrap gap-1.5">
                            {video.flagTypes.map((risk, idx) => (
                              <Badge key={`${video.videoId}-${risk}-${idx}`} color="gray" size="sm">
                                {risk}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Stats (no padding) */}
                      <div>
                        <div className="border-t border-border-primary" />
                        <div className="grid grid-cols-4 gap-4 p-4">
                          <div>
                            <div className="text-xs text-tertiary mb-1 text-center">GMV</div>
                            <div className="text-sm font-medium text-primary text-center">
                              {currencyFormatter.format(video.gmv)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-tertiary mb-1 text-center">Views</div>
                            <div className="text-sm font-medium text-primary text-center">
                              {numFormatter.format(video.views)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-tertiary mb-1 text-center">GPM</div>
                            <div className="text-sm font-medium text-primary text-center">
                              {currencyFormatter.format(video.gpm)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-tertiary mb-1 text-center">Sales</div>
                            <div className="text-sm font-medium text-primary text-center">{video.unitSales}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-tertiary">No videos with risks detected</div>
          )}

          {/* Note about limited data */}
          {brandTaggedVideos.length > 0 && brandTaggedVideos.length < data.safetyAnalysis.videosWithRisks && (
            <div className="text-xs text-tertiary italic">
              Showing {brandTaggedVideos.length} of {data.safetyAnalysis.videosWithRisks} videos with risks
            </div>
          )}

          {/* See all Brand content link */}
          {brandTaggedVideos.length > 0 && (
            <Link
              to={videoExplorerRoute.to}
              params={{ merchantId: merchant }}
              search={{
                creator: [
                  {
                    id: data.creatorId,
                    label: creatorNames.nickname ?? creatorNames.handle ?? data.creatorName,
                  },
                ],
              }}
              className="flex items-center gap-1 text-sm text-secondary hover:text-primary font-medium transition-colors"
            >
              See all Brand content from this creator
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
